import { useCallback, type MutableRefObject } from "react";
import { format } from "date-fns";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { logError } from "@/lib/sentry";
import * as orderService from "@/supabase/order/service/orderService";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { MemberSetting } from "@/types/purchase";
import { UseMessageTemplatesResult } from "@/hooks/useMessageTemplates";

// Home.tsx의 모듈 상수와 동일 값(byte-preservation). 정본 정의는 Home.tsx L45.
const DEFAULT_CUSTOMER_NICKNAME = "고객 닉네임";

interface UseCustomerCrudProps {
  user: User | null;
  localDateOrderTemplates: CustomerTemplate[];
  activeCustomerId: string | null;
  displayTemplate: string;
  customerTemplates: CustomerTemplate[];
  setLocalDateOrderTemplates: (
    updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])
  ) => void;
  setActiveCustomerIdState: (id: string | null) => void;
  _setDisplayTemplate: (template: string) => void;
  getDefaultCustomer: () => CustomerTemplate | null;
  removeOrderAndUpdateTemplates: (orderId: number) => Promise<boolean>;
  removeLocalCustomerOnly: (customerId: string) => void;
  refreshOrders: () => Promise<void>;
  messageTemplates: UseMessageTemplatesResult;
  memberSetting: MemberSetting;
  extractOrderId: (customerId: string) => number | null;
  removeSessionCustomer?: (customerId: string) => void;
  selectedDate: Date;
  // P15b add+edit 합류 — 신규 input
  // ★ pendingCreatedOrderIdsRef는 Home 소유 단일 인스턴스(Home L187)를 주입. 훅 자체 ref 생성 절대 금지
  //   (새 ref면 fresh 고객 생성 직후 날짜 전환 시 신규 주문 소실 — 데이터 무결성 빅뱅, §6 게이트4).
  pendingCreatedOrderIdsRef: MutableRefObject<Set<string>>;
  addSessionCustomer?: (nickname: string) => string;
  updateSessionCustomerNickname?: (customerId: string, newNickname: string) => void;
  // edit의 dep 배열에만 존재하는 vestigial dep(body 미참조). exhaustive-deps "정리" 금지 — 바이트 보존.
  updateDefaultCustomerNickname: (newNickname: string, customerIdToUpdate?: string) => void;
}

interface UseCustomerCrudResult {
  handleCustomerAddRequest: (nickname: string) => Promise<string | undefined>;
  handleCustomerRemoveRequest: (
    customerId: string,
    options?: { skipConfirm?: boolean }
  ) => Promise<void>;
  handleCustomerEditRequest: (customerId: string, newNickname: string) => Promise<void>;
}

/**
 * 고객 CRUD 도메인 훅(P15a remove + P15b add/edit). Home의 핸들러 3개를 바이트 그대로 이동.
 * body·dep 배열 원본 동일. handleCustomerMemoChange는 범위 밖(memo-editing hazard, §7).
 *
 * 바이트보존 high 계약(잘못 바꾸면 빌드는 통과해도 런타임 데이터 조용히 오류 — 최대표면 phase):
 * [remove]
 * - nextActiveId 인덱스 계약: 활성고객 삭제 시 removedCustomerIndex 기준으로 "직전 이웃(idx-1)"을
 *   고르되 idx<=0 또는 경계 밖이면 [0] 폴백, 리스트 빈 경우만 getDefaultCustomer. 비활성-활성고객이
 *   목록에서 사라진 경우의 두 번째 분기도 보존(합치지 말 것).
 * - 옵티미스틱 롤백: previousLocalDateOrderTemplates / previousActiveCustomerId / previousTemplate
 *   3개를 미리 캡처 → DB 실패 시 셋 모두 복원 + refreshOrders(). 부분 복원 금지.
 * - setTemplate 폴백 순서: customer.template > messageTemplates.activeTemplate?.content >
 *   memberSetting.messageTemplate > "" (L552 effect의 customer.template > active > memberSetting 순서와 동일).
 * - skipConfirm 옵션 + legacy window.confirm 양쪽 보존(기본 흐름은 Dialog가 skipConfirm:true로 호출).
 * - order_ 접두 분기: removeOrderAndUpdateTemplates(DB) vs removeLocalCustomerOnly(로컬) — extractOrderId 가드.
 * [add]
 * - pendingCreatedOrderIdsRef.current.add(newCustomerId): 서버 재조회 반영 전 머지 삭제 방지. Home 단일 인스턴스 필수.
 * - 신규 고객 template seed = messageTemplates.activeTemplate?.content > memberSetting.messageTemplate > "".
 * - 비회원 경로(addSessionCustomer) 보존 — 회원/비회원 분기 합치지 말 것.
 * [edit]
 * - 닉네임 옵티미스틱 업데이트 → DB 실패 시 oldNickname으로 롤백(양쪽 catch 분기 모두).
 * - default_ 고객/기본닉네임 → createEmptyOrder로 승격(items/template/memo 보존 + pendingRef 등록). add와 동일 stale 방지.
 * - vestigial dep updateDefaultCustomerNickname(body 미참조) 유지. selectedDate/extractOrderId/setActiveCustomerIdState는
 *   body에서 read하지만 dep에 없음 — 원본 그대로(exhaustive-deps "정리" 금지).
 *
 * dep 배열 바이트 보존: remove의 selectedDate는 body 미참조이나 원본 dep에 있어 그대로 유지.
 * messageTemplates는 remove dep에서 의도적 누락 — body에서 read하지만 dep에 추가하지 말 것(원본 동작 보존).
 */
export function useCustomerCrud({
  user,
  localDateOrderTemplates,
  activeCustomerId,
  displayTemplate,
  customerTemplates,
  setLocalDateOrderTemplates,
  setActiveCustomerIdState,
  _setDisplayTemplate,
  getDefaultCustomer,
  removeOrderAndUpdateTemplates,
  removeLocalCustomerOnly,
  refreshOrders,
  messageTemplates,
  memberSetting,
  extractOrderId,
  removeSessionCustomer,
  selectedDate,
  pendingCreatedOrderIdsRef,
  addSessionCustomer,
  updateSessionCustomerNickname,
  updateDefaultCustomerNickname,
}: UseCustomerCrudProps): UseCustomerCrudResult {
  const handleCustomerAddRequest = useCallback(async (nickname: string): Promise<string | undefined> => {
    if (user) {
      try {
        const orderDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
        const newOrderId = await orderService.createEmptyOrder(user.id, nickname, orderDate);
        const newCustomerId = `order_${newOrderId}`;

        const newCustomer: CustomerTemplate = {
          id: newCustomerId,
          nickname: nickname,
          // 다중 템플릿 도입 후 신규 고객은 현재 active 탭 content로 seed.
          // (옛 컬럼은 dual-write 호환용이라 신규 sourcing에는 부적절)
          template: messageTemplates.activeTemplate?.content || memberSetting.messageTemplate || "",
          items: [],
          is_keep: false,
          status: "미지정",
          shippingFeeOverride: null,
          paymentMethod: null,
          memo: "",
          createdAt: new Date().toISOString(),
          totalPrice: 0,
          order_date: orderDate
        };

        // 서버 재조회가 이 주문을 반영하기 전까지 머지에서 삭제되지 않도록 보존 대상에 등록
        pendingCreatedOrderIdsRef.current.add(newCustomerId);
        setLocalDateOrderTemplates(prev => [...prev, newCustomer]);
        setActiveCustomerIdState(newCustomerId);
        return newCustomerId;
      } catch (error) {
        logError("Failed to create empty order", error);
        toast.error("고객 추가에 실패했습니다.");
        return undefined;
      }
    } else if (addSessionCustomer) {
      const newSessionCustomerId = addSessionCustomer(nickname);
      setActiveCustomerIdState(newSessionCustomerId);
      return newSessionCustomerId;
    }
    return undefined;
    // pendingCreatedOrderIdsRef는 Home의 useRef(안정 참조)를 prop으로 받으므로 dep 추가는 동작상 inert
    // (참조 동일성 불변 → useCallback 재생성 안 함). prop으로 바뀌며 exhaustive-deps가 요구해 lint parity 위해 명시.
  }, [user, selectedDate, memberSetting.messageTemplate, messageTemplates.activeTemplate?.content, setLocalDateOrderTemplates, setActiveCustomerIdState, addSessionCustomer, pendingCreatedOrderIdsRef]);

  const handleCustomerRemoveRequest = useCallback(async (customerId: string, options?: { skipConfirm?: boolean }) => {
    const customerToRemove = customerTemplates.find(c => c.id === customerId);
    if (!customerToRemove) {
      return;
    }
    // 삭제 확인(기본 confirm은 옵션으로만 사용, 기본 흐름은 Dialog 사용)
    if (!options?.skipConfirm) {
      const shippingNotice = customerId.startsWith('order_') ? '\n배송엑셀에 입력한 이 고객의 배송정보도 함께 삭제됩니다.' : '';
      const confirmMsg = `'${customerToRemove.nickname}' 고객을 삭제하시겠어요?\n삭제 시 상품/메모/상태가 함께 삭제되며 되돌릴 수 없습니다.${shippingNotice}`;
      const confirmed = window.confirm(confirmMsg);
      if (!confirmed) return;
    }
    if (user) {
      const previousLocalDateOrderTemplates = [...localDateOrderTemplates];
      const previousActiveCustomerId = activeCustomerId;
      const previousTemplate = displayTemplate;

      const newLocalDateOrderTemplates = localDateOrderTemplates.filter(c => c.id !== customerId);
      setLocalDateOrderTemplates(newLocalDateOrderTemplates);

      let nextActiveId = activeCustomerId;
      const removedCustomerIndex = previousLocalDateOrderTemplates.findIndex(c => c.id === customerId);

      if (activeCustomerId === customerId) {
        if (newLocalDateOrderTemplates.length > 0) {
          if (removedCustomerIndex > 0 && removedCustomerIndex <= newLocalDateOrderTemplates.length) {
            nextActiveId = newLocalDateOrderTemplates[removedCustomerIndex - 1].id;
          } else {
            nextActiveId = newLocalDateOrderTemplates[0].id;
          }
        } else {
          const defaultCustomer = getDefaultCustomer();
          nextActiveId = defaultCustomer ? defaultCustomer.id : null;
        }
      } else if (activeCustomerId && !newLocalDateOrderTemplates.some(c => c.id === activeCustomerId)) {
        if (newLocalDateOrderTemplates.length > 0) {
          nextActiveId = newLocalDateOrderTemplates[0].id;
        } else {
          const defaultCustomer = getDefaultCustomer();
          nextActiveId = defaultCustomer ? defaultCustomer.id : null;
        }
      }

      setActiveCustomerIdState(nextActiveId);

      if (nextActiveId) {
        const nextActiveCustObj = newLocalDateOrderTemplates.find(c => c.id === nextActiveId);
        // items는 activeCustomer.items에서 derive (source 갱신됨) — 별도 set 불필요.
        // customer.template > active > memberSetting (line 552 effect와 동일 순서)
        _setDisplayTemplate(nextActiveCustObj?.template || messageTemplates.activeTemplate?.content || memberSetting.messageTemplate || "");
      } else {
        _setDisplayTemplate(messageTemplates.activeTemplate?.content || memberSetting.messageTemplate || "");
      }

      let dbSuccess = false;
      try {
        if (customerId.startsWith('order_')) {
          const orderId = extractOrderId(customerId);
          if (orderId) {
            dbSuccess = await removeOrderAndUpdateTemplates(orderId);
          } else {
            dbSuccess = false;
          }
        } else {
          removeLocalCustomerOnly(customerId);
          dbSuccess = true;
        }
      } catch (error) {
        logError("[Home.tsx] 고객 삭제 중 DB/로컬 작업 오류:", error);
        dbSuccess = false;
      }

      if (!dbSuccess) {
        alert("고객 삭제에 실패했습니다. 이전 상태로 복원합니다.");
        setLocalDateOrderTemplates(previousLocalDateOrderTemplates);
        setActiveCustomerIdState(previousActiveCustomerId);
        // items는 source(localDateOrderTemplates) 복원으로 자동 derive.
        _setDisplayTemplate(previousTemplate);
        refreshOrders();
      }
    } else if (removeSessionCustomer) {
      removeSessionCustomer(customerId);
    }
  }, [user, localDateOrderTemplates, activeCustomerId, displayTemplate, customerTemplates,
    setLocalDateOrderTemplates, setActiveCustomerIdState, _setDisplayTemplate,
    getDefaultCustomer, removeOrderAndUpdateTemplates, removeLocalCustomerOnly, refreshOrders,
    memberSetting.messageTemplate, removeSessionCustomer, selectedDate]);

  const handleCustomerEditRequest = useCallback(async (customerId: string, newNickname: string) => {
    const customerToEdit = customerTemplates.find(c => c.id === customerId);
    if (!customerToEdit) {
      return;
    }
    const oldNickname = customerToEdit.nickname;

    if (user) {
      setLocalDateOrderTemplates(prev =>
        prev.map(cust => cust.id === customerId ? { ...cust, nickname: newNickname } : cust)
      );

      const orderId = extractOrderId(customerId);
      if (orderId) {
        try {
          await orderService.updateOrderNickname(orderId, newNickname);
        } catch (error) {
          logError(`[Home.tsx] DB 주문 닉네임 업데이트 실패 (orderId ${orderId}):`, error);
          alert("주문 닉네임 변경 실패. UI를 이전 상태로 복구합니다.");
          setLocalDateOrderTemplates(prev =>
            prev.map(cust => cust.id === customerId ? { ...cust, nickname: oldNickname } : cust)
          );
        }
      } else if (customerId.startsWith('default_') || customerToEdit.nickname === DEFAULT_CUSTOMER_NICKNAME) {
        try {
          const orderDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
          const newOrderId = await orderService.createEmptyOrder(user.id, newNickname, orderDate);
          const newCustomerId = `order_${newOrderId}`;
          // 서버 재조회 반영 전 stale 조회로 인한 머지 삭제 방지 (handleCustomerAddRequest와 동일)
          pendingCreatedOrderIdsRef.current.add(newCustomerId);

          setLocalDateOrderTemplates(prev => {
            const newTemplates = prev.filter(c => c.id !== customerId);
            const totalPrice = customerToEdit.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            newTemplates.push({
              id: newCustomerId,
              nickname: newNickname,
              template: customerToEdit.template,
              items: customerToEdit.items,
              is_keep: false,
              status: "미지정",
              shippingFeeOverride: null,
              paymentMethod: customerToEdit.paymentMethod ?? null,
              memo: customerToEdit.memo || "",
              totalPrice,
              order_date: orderDate
            });
            return newTemplates;
          });

          setActiveCustomerIdState(newCustomerId);

        } catch (error) {
          logError("Failed to convert default customer to order", error);
          toast.error("고객 정보 변경에 실패했습니다.");
          // Revert UI changes
          setLocalDateOrderTemplates(prev =>
            prev.map(cust => cust.id === customerId ? { ...cust, nickname: oldNickname } : cust)
          );
        }
      }
    } else if (updateSessionCustomerNickname) {
      updateSessionCustomerNickname(customerId, newNickname);
    }
  }, [user, customerTemplates, setLocalDateOrderTemplates, updateDefaultCustomerNickname, updateSessionCustomerNickname]);

  return { handleCustomerAddRequest, handleCustomerRemoveRequest, handleCustomerEditRequest };
}
